import { getClientMetadata } from '../utils/clientMetadata';
import { MCP_LOGIN_STORAGE_KEY } from '../config/security';
import { getEncrypted } from './core/cryptoStorage';

export class MessageService {
  constructor(licenseToken, sessionToken, httpClient) {
    this.licenseToken = licenseToken;
    this.sessionToken = sessionToken;
    this.httpClient = httpClient;
  }

  async sendMessage(message, onChunk, conversationId = null) {
    const baseURL = this.httpClient.baseURL;
    const url = `${baseURL}/ask`;

    const requestMetadata = getClientMetadata();

    const requestBody = {
      message,
      session_token: this.sessionToken,
      request_metadata: requestMetadata
    };

    if (conversationId) {
      requestBody.conversation_id = conversationId;
    }

    const mcpLoginData = await getEncrypted(MCP_LOGIN_STORAGE_KEY);
    if (mcpLoginData) {
      requestBody.mcp_login_data = mcpLoginData;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.licenseToken}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(JSON.stringify({
          statusCode: response.status,
          errorCode: errorData.error_code || 'UNKNOWN_ERROR',
          message: errorData.message || 'Request failed',
          retryAfter: errorData.retry_after_seconds || null
        }));
      }

      return await this._processSSEResponse(response, onChunk);

    } catch (error) {
      if (error.message.startsWith('{')) {
        throw error;
      }
      throw new Error(JSON.stringify({
        statusCode: 500,
        errorCode: 'NETWORK_ERROR',
        message: error.message,
        retryAfter: null
      }));
    }
  }


  async _processSSEResponse(response, onChunk) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullResponse = '';
    let metadata = null;
    let summary = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.trim() || !line.startsWith('data: ')) continue;

        const data = line.replace('data: ', '').trim();
        if (data === '[DONE]') continue;

        try {
          const event = JSON.parse(data);

          if (event.type === 'content') {
            fullResponse += event.text;
            if (onChunk) onChunk(event.text);
          } else if (event.type === 'metadata') {
            metadata = event;
          } else if (event.type === 'summary') {
            summary = event;
          } else if (event.type === 'error') {
            throw new Error(JSON.stringify({
              statusCode: event.status_code || 500,
              errorCode: event.error_code,
              message: event.message,
              retryAfter: null
            }));
          }
        } catch (parseError) {
          if (parseError.message && parseError.message.startsWith('{')) {
            throw parseError;
          }
          console.error('[MessageService] Error parsing SSE event:', parseError);
        }
      }
    }

    if (!summary) {
      throw new Error('No summary received from server');
    }

    return {
      success: true,
      data: {
        message_id: summary.message_id,
        conversation_id: summary.conversation_id,
        response: fullResponse,
        response_time_ms: summary.response_time_ms,
        model_used: summary.model_used,
        created_at: summary.created_at
      }
    };
  }


  async transcribeAudio(audioBlob) {
    const formData = new FormData();
    formData.append('file', audioBlob, 'audio.webm');

    const client = this.httpClient.getClient();
    const response = await client.post('/transcribe', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  }
}
